import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { Button } from './button';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export function SearchableSelect({ 
  value, 
  onValueChange, 
  options = [], 
  placeholder = "Search or add new...",
  allowNew = true,
  className 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState(value || '');
  const containerRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    if (!isOpen) setIsOpen(true);
    
    if (allowNew) {
      onValueChange(newValue);
    }
  };

  const handleOptionSelect = (option) => {
    setInputValue(option);
    onValueChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (filteredOptions.length === 1) {
        handleOptionSelect(filteredOptions[0]);
      } else if (allowNew && inputValue.trim()) {
        onValueChange(inputValue.trim());
        setIsOpen(false);
      }
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const clearValue = () => {
    setInputValue('');
    onValueChange('');
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="pr-16"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          {inputValue && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearValue}
              className="h-6 w-6 p-0 hover:bg-transparent"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
            className="h-6 w-6 p-0 hover:bg-transparent"
          >
            <ChevronDown className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")} />
          </Button>
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors",
                  option === value && "bg-accent text-accent-foreground"
                )}
                onClick={() => handleOptionSelect(option)}
              >
                <span>{option}</span>
                {option === value && <Check className="h-4 w-4" />}
              </button>
            ))
          ) : searchTerm && allowNew ? (
            <button
              type="button"
              className="w-full flex items-center px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
              onClick={() => handleOptionSelect(searchTerm)}
            >
              <span>"{searchTerm}" ekle</span>
            </button>
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {allowNew ? "Type to add new option" : "No options found"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}